import { applySnapshot, flow, types, getSnapshot } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { PUBLISH_STATUSES } from "constants/models";
import { MSURL } from "utils/ms";
import { getNextPublishStatusWithoutScheduled } from "utils/publish";
import { TagDictionaryItem } from "./dictionary/TagDictionary";
import { ClaimModel } from "./ClaimStore";

const pathPattern = "/pattern/api/v1";
type RawData = {
  items: Array<any>;
};

type DictionaryProperties = {
  fetch: (params?: any) => Promise<RawData>;
  getLabel: (item: any) => string;
  getValue: (item: any) => any;
  itemModel: any;
  extraItems?: Array<any>;
};

function createArrayDictionary({
  fetch,
  getValue,
  getLabel,
  itemModel,
  extraItems,
}: DictionaryProperties) {
  return types
    .model({
      items: types.array(itemModel),
      isLoading: types.optional(types.boolean, false),
    })
    .views(self => ({
      get options() {
        return [...(extraItems ?? []), ...self.items].map(item => ({
          value: getValue(item),
          label: getLabel(item),
        }));
      },
    }))
    .actions(self => ({
      fetch: flow(function*(...params: any) {
        try {
          self.isLoading = true;
          const data = yield fetch(...params);
          const obj = {
            items: data,
            isLoading: self.isLoading,
          };
          applySnapshot(self, obj);
          return obj;
        } catch (error) {
          console.log(error);
        } finally {
          self.isLoading = false;
        }
      }),
    }));
}

const GenderDictionaryItem = types.model({
  id: types.number,
  title: types.string,
});

const ActivityLevelDictionaryItem = types.model({
  id: types.number,
  description: types.string,
});

const AgeRangeDictionaryItem = types.model({
  id: types.number,
  name: types.string,
});

export const AgeRangeDictionary = createArrayDictionary({
  itemModel: AgeRangeDictionaryItem,
  fetch: async function() {
    return await api.get(
      `${pathPattern}/list/age-range`,
      undefined,
      undefined,
      MSURL,
    );
  },
  getValue: value => value.id,
  getLabel: ({ name }) => name,
}).create();

const WeightGoalTypeDictionaryItem = types.model({
  id: types.number,
  name: types.string,
});

export const WeightGoalTypeDictionary = createArrayDictionary({
  itemModel: WeightGoalTypeDictionaryItem,
  fetch: async function() {
    return await api.get(
      `${pathPattern}/list/weight-goal-type`,
      undefined,
      undefined,
      MSURL,
    );
  },
  getValue: value => value.id,
  getLabel: ({ name }) => name,
}).create();

export const GenderDictionary = createArrayDictionary({
  itemModel: GenderDictionaryItem,
  fetch: async function() {
    return await api.get(
      `${pathPattern}/list/sex`,
      undefined,
      undefined,
      MSURL,
    );
  },
  getValue: value => value.id,
  getLabel: ({ title }) => title,
}).create();

export const ActivityLevelDictionary = createArrayDictionary({
  itemModel: ActivityLevelDictionaryItem,
  fetch: async function() {
    return await api.get(
      `${pathPattern}/list/activity-level`,
      undefined,
      undefined,
      MSURL,
    );
  },
  getValue: value => value.id,
  getLabel: ({ description }) => description,
}).create();

export const PatternModel = types
  .model({
    id: types.number,
    title: types.string,
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }));

export const PatternDetailsStore = types
  .model({
    id: types.optional(types.number, -1),
    patternId: types.optional(types.number, -1),
    sexId: types.optional(types.number, -1),
    activityLevelId: types.optional(types.number, -1),
    ageRangeId: types.optional(types.number, -1),
    weightGoalTypeId: types.optional(types.number, -1),
    isPatternLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }))
  .actions(self => ({
    editPatternDetail: flow(function*(payload: any) {
      self.isPatternLoading = true;

      try {
        return yield api.put(
          `${pathPattern}/pattern/${payload.id}/detail`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        self.isPatternLoading = false;
      }
    }),
    getData: flow(function*(id) {
      const response = yield api.get(
        `${pathPattern}/pattern/${id}/detail`,
        undefined,
        undefined,
        MSURL,
      );

      self.id = response.id;
      self.patternId = response.patternID;
      self.sexId = response.sexID;
      self.activityLevelId = response.activityLevelID;
      self.ageRangeId = response.ageRangeID;
      self.weightGoalTypeId = response.weightGoalTypeID;

      return self;
    }),
  }))
  .create();

const PatternDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    sexId: types.maybe(types.number),
    activityLevelId: types.maybe(types.number),
    ageRangeId: types.maybe(types.number),
    weightGoalTypeId: types.maybe(types.number),
    tags: types.array(TagDictionaryItem),
    claims: types.array(ClaimModel),
  })
  .views(self => ({
    get formData() {
      const uniqueTagsMap = new Map();
      self.tags.map(item => uniqueTagsMap.set(item.id, item));
      const tagArray = Array.from(uniqueTagsMap.values());

      const uniqueClaimsMap = new Map();
      self.claims.map(item => uniqueClaimsMap.set(item.id, item));
      const claimArray = Array.from(uniqueClaimsMap.values());

      return {
        ...self,
        tags: tagArray.map(({ id, title }) => ({
          id,
          name: title,
        })),
        claims: claimArray.map(({ id, title }) => ({
          id,
          name: title,
        })),
      };
    },
  }))
  .props({
    isPatternLoading: types.optional(types.boolean, false),
  });

const ContentPatternStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathPattern}/pattern`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: PatternModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(PatternDetailsModel),
    isMoreLoading: types.optional(types.boolean, false),
    isPatternLoading: types.optional(types.boolean, false),
    isPatternManaging: types.optional(types.boolean, false),
    isPatternPublishing: types.optional(types.boolean, false),
    isPatternCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    updatePattern(id: number, data: any) {
      const { items } = getSnapshot(self);

      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...items[index], ...data };
      }
    },

    fetchDetailOptions: flow(function*(id?: number) {
      const response = yield api.get(
        `${pathPattern}/pattern/${id}/detail`,
        undefined,
        undefined,
        MSURL,
      );

      applySnapshot(self, {
        ...self,
        details: {
          id: response.id,
          title: self.details?.title || "",
          publishStatus: self.details?.publishStatus || "",
          sexId: response.sexID,
          activityLevelId: response.activityLevelID,
          ageRangeId: response.ageRangeID,
          weightGoalTypeId: response.weightGoalTypeID,
          claims: response.claims,
          tags: response.tags
            ? response.tags.map((t: any) => ({
                id: t.id,
                title: `${t.title}`,
              }))
            : [],
        },
      });

      return self;
    }),
  }))
  .actions(self => ({
    fetchMorePatterns: flow(function*() {
      self.isPatternLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);
          const { items } = yield api.get(
            `${pathPattern}/pattern`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPatternLoading = false;
      }
    }),

    fetchPatternDetails: flow(function*(id?: number) {
      try {
        self.isPatternLoading = true;
        const details = yield api.get(
          `${pathPattern}/pattern/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details: {
            ...self.details,
            ...details,
          },
        });

        yield self.fetchDetailOptions(id);

        return self.details;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPatternLoading = false;
      }
    }),

    editPatternDetailInfo: flow(function*(payload: any) {
      self.isPatternManaging = true;

      try {
        return yield api.put(
          `${pathPattern}/pattern/${payload.patternID}/detail`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPatternManaging = false;
      }
    }),

    editPatternInfo: flow(function*(payload: any) {
      self.isPatternManaging = true;

      try {
        return yield api.put(
          `${pathPattern}/pattern/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPatternManaging = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },

    createPattern: flow(function*(payload: any) {
      self.isPatternManaging = true;

      try {
        const { id } = yield api.post(
          `${pathPattern}/pattern`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPatternManaging = false;
      }
    }),

    changePatternStatus: flow(function*(id: number, status: string) {
      try {
        self.isPatternPublishing = true;
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${pathPattern}/pattern/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updatePattern(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPatternPublishing = false;
      }
    }),
  }));

export default ContentPatternStore.create();
